import type { ButtonHTMLAttributes, AnchorHTMLAttributes, ReactNode } from "react";
import { cx } from "@/lib/cx";

export type ButtonVariant = "ink" | "accent" | "outline" | "ghost";
export type ButtonSize = "sm" | "md" | "lg";

type Common = {
  children?: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Rendered after the label. Hover widens the gap 8px → 16px. */
  icon?: ReactNode;
  href?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
};

export type ButtonProps = Common &
  Omit<ButtonHTMLAttributes<HTMLButtonElement> & AnchorHTMLAttributes<HTMLAnchorElement>, keyof Common>;

const sizes: Record<ButtonSize, string> = {
  sm: "px-[14px] py-2 text-[13px]",
  md: "px-5 py-3 text-[14px]",
  lg: "px-[28px] py-4 text-[15px]",
};

const variants: Record<ButtonVariant, string> = {
  ink: "bg-surface-ink text-on-ink border-border-on-ink shadow-ink hover:bg-surface-ink-hover",
  accent: "bg-signal-500 text-on-ink border-signal-600 hover:bg-signal-600",
  outline: "bg-transparent text-text-primary border-border-strong hover:border-text-primary",
  ghost: "bg-transparent text-text-body border-transparent hover:text-signal-500",
};

export function Button({
  children,
  variant = "ink",
  size = "md",
  icon = null,
  href,
  disabled = false,
  fullWidth = false,
  className,
  ...rest
}: ButtonProps) {
  const classes = cx(
    "inline-flex items-center justify-center gap-2 font-sans font-semibold tracking-[0.01em]",
    "border rounded-md cursor-pointer no-underline whitespace-nowrap",
    "transition-[background-color,color,border-color,gap,opacity] duration-160 ease-out",
    // The CTA's icon gap widens on hover — the one flourish Button is allowed.
    icon && !disabled && "hover:gap-4",
    variant === "ghost" ? "px-0 py-[6px] rounded-none" : sizes[size],
    variants[variant],
    fullWidth ? "w-full" : "w-auto",
    disabled && "opacity-[0.42] pointer-events-none",
    className,
  );

  if (href) {
    return (
      <a href={href} className={classes} aria-disabled={disabled || undefined} {...rest}>
        {children}
        {icon}
      </a>
    );
  }

  return (
    <button type="button" className={classes} disabled={disabled} {...rest}>
      {children}
      {icon}
    </button>
  );
}
